/**
 * Utility for building URLSearchParams with support for arrays and single values
 */
export const buildSearchParams = (config: {
  single?: Record<string, any>;
  arrays?: Record<string, any[]>;
  arrayKeyMap?: Record<string, string>;
}): URLSearchParams => {
  const params = new URLSearchParams();

  // Handle single values
  if (config.single) {
    Object.entries(config.single).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value));
      }
    });
  }

  // Handle arrays with optional key mapping
  if (config.arrays) {
    Object.entries(config.arrays).forEach(([key, values]) => {
      if (values && values.length > 0) {
        const targetKey = config.arrayKeyMap?.[key] || key;
        values.forEach((value) => params.append(targetKey, String(value)));
      }
    });
  }

  return params;
};

/**
 * Simplified utility that automatically categorizes params as single or array
 */
export const buildSearchParamsAuto = (
  params: Record<string, any>,
  arrayKeyMap?: Record<string, string>
): URLSearchParams => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;

    if (Array.isArray(value)) {
      // Handle arrays
      if (value.length > 0) {
        const targetKey = arrayKeyMap?.[key] || key;
        value.forEach((item) => searchParams.append(targetKey, String(item)));
      }
    } else if (typeof value === 'object' && value !== null) {
      // Handle objects (like dateRange: { gte: 2024, lte: 2024 }) by JSON stringifying
      searchParams.append(key, JSON.stringify(value));
    } else {
      // Handle single values
      if (value !== '') {
        searchParams.append(key, String(value));
      }
    }
  });

  return searchParams;
};